import { useEffect, useRef, useState } from 'react'

interface UseOverlayOptions {
  isOpen: boolean
  isMobile: boolean
  animationDuration?: number
}

interface UseOverlayReturn {
  shouldRenderOverlay: boolean
  overlayState: 'open' | 'closing'
}

/**
 * Хук для управления overlay с анимацией закрытия
 * Решает проблему unmount элемента до завершения анимации
 */
export function useOverlay({
  isOpen,
  isMobile,
  animationDuration = 280,
}: UseOverlayOptions): UseOverlayReturn {
  const [isOverlayMounted, setOverlayMounted] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const closeTimerRef = useRef<number | null>(null)
  const wasOpenRef = useRef(false)

  const shouldRenderOverlay = isMobile && isOverlayMounted
  const overlayState = isClosing ? 'closing' : 'open'

  // Управление состоянием overlay
  useEffect(() => {
    // Очищаем предыдущий таймер
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    const shouldOpen = isMobile && isOpen

    // Открываем overlay
    if (shouldOpen) {
      wasOpenRef.current = true
      setOverlayMounted(true)
      setIsClosing(false)
      return
    }

    // Закрываем overlay с анимацией (только если был открыт ранее)
    if (wasOpenRef.current) {
      wasOpenRef.current = false
      setIsClosing(true)
      closeTimerRef.current = window.setTimeout(() => {
        setOverlayMounted(false)
        setIsClosing(false)
        closeTimerRef.current = null
      }, animationDuration)
    }
  }, [isMobile, isOpen, animationDuration])

  // Очистка при unmount
  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    },
    []
  )

  return {
    shouldRenderOverlay,
    overlayState,
  }
}
